import { MenuItem } from "@/redux/services/menu/menu.type";
import { OrderType } from "@/redux/services/order/order.type";

export type CartEntryType = "item" | "combo" | "daily_dish" | "formula";

export interface FormulaCartSelection {
  menuFormulaComponentItemId: string;
  menuItemId?: string;
  selectedOptions?: string[];
}

export interface CartEntry {
  id: string;
  type: CartEntryType;
  menuItem?: MenuItem;
  menuComboId?: string;
  dailyDishId?: string;
  menuFormulaId?: string;
  name: string;
  imageUrl?: string;
  quantity: number;
  selectedOptionValueIds: string[];
  selectedOptionLabels: string[];
  additionalDemands: string;
  unitPrice: number;
  comboSelections?: { menuItem: string; sideType?: string; selectedOptions?: string[] }[];
  formulaSelections?: FormulaCartSelection[];
  /**
   * When this entry was hydrated from an existing order line (edit mode), the
   * line's `@id` IRI. Echoing it back on PATCH keeps the line as-is (id + kitchen
   * status preserved). New entries leave this undefined and are sent as objects.
   */
  existingIri?: string;
  /** Quantity of the existing line at hydration time, to detect changes. */
  originalQuantity?: number;
}

export interface DraftOrder {
  id: string;
  orderType: OrderType;
  tableId?: string;
  notes?: string;
  /** Optional reason captured when editing an existing order (edit mode only). */
  modificationReason?: string;
  cart: CartEntry[];
  createdAt: number;
  /**
   * When set, this draft is editing an existing server-side order; on submit,
   * the page calls updateOrder instead of createPosOrder. Drafts persistence
   * skips edit-mode drafts.
   */
  editingOrderId?: string;
  // Delivery fields — only meaningful when orderType === "delivery". Hydrated
  // from the order being edited so staff can adjust them inline.
  deliveryAddress?: string;
  /** Stored in cents to match the order's totalPrice / price model. */
  deliveryFee?: number;
  deliveryPersonName?: string;
  deliveryPersonPhone?: string;
  /** Number of guests served — dine-in only, when the restaurant asks for it. */
  guestCount?: number;
}

export const ORDER_TYPE_LABELS: Record<OrderType, string> = {
  dine_in: "Sur place",
  takeaway: "À emporter",
  delivery: "Livraison",
};

export function buildCartEntryId(
  itemId: string,
  type: CartEntryType,
  optionValueIds: string[],
  additionalDemands: string
): string {
  return `${type}::${itemId}::${[...optionValueIds].sort().join(",")}::${additionalDemands}`;
}

let draftOrderCounter = 0;
export function createDraftOrderId(): string {
  return `draft-${++draftOrderCounter}-${Date.now()}`;
}
